import { useEffect, useState } from "react";
import type { FC, ChangeEventHandler, FormEventHandler } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import {
  Button,
  CircularProgress,
  InputLabel,
  OutlinedInput,
  Stack,
} from "@mui/material";
import { useShallowPickSelector } from "store/useSelector";
import { BottomSheet } from "components/atoms/BottomSheet";
import { appActions } from "store/app/app.slice";
import { useEditPasswordMutation } from "store/current-user/current-user.api";
import { currentUserActions } from "store/current-user/current-user.slice";
import { toAuth } from "utils/text";

export const EditUserPasswordMenu: FC = () => {
  const [oldPassword, setOldPassword] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [passwordConfirm, setPasswordConfirm] = useState<string>("");

  const { editUserPasswordMenu } = useShallowPickSelector("app", [
    "editUserPasswordMenu",
  ]);
  const { user } = useShallowPickSelector("currentUser", ["user"]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const _location = useLocation();

  const [editPassword, { isLoading, isSuccess, isError }] =
    useEditPasswordMutation();

  useEffect(() => {
    if (isSuccess && user) {
      dispatch(currentUserActions.setAuth(toAuth(user.userName, password))); // WARN replace password with token
      onClose();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      dispatch(
        appActions.setSnackbar({
          isOpen: true,
          message: "رمز عبور فعلی اشتباه است",
          severity: "error",
        })
      );
    }
  }, [isError]);

  const onClose = () => {
    dispatch(
      appActions.setEditUserPasswordMenu({
        isOpen: false,
      })
    );
  };

  const onChangeOldPassword: ChangeEventHandler<HTMLInputElement> = (event) => {
    let value = event.target.value;
    setOldPassword(value);
  };
  const onChangePassword: ChangeEventHandler<HTMLInputElement> = (event) => {
    let value = event.target.value;
    setPassword(value);
  };
  const onChangePasswordConfirm: ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    let value = event.target.value;
    setPasswordConfirm(value);
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (!user || password.length === 0) return;
    editPassword({
      body: { password, currentPassword: oldPassword },
    });
  };

  return (
    <BottomSheet
      title={"ویرایش رمز عبور"}
      isOpen={editUserPasswordMenu.isOpen}
      onClose={onClose}
    >
      <Stack component={"form"} onSubmit={onSubmit}>
        <Stack mx={3} mt={1} mb={6}>
          <InputLabel htmlFor={"old-password"}>رمز عبور فعلی</InputLabel>
          <OutlinedInput
            id={"old-password"}
            value={oldPassword}
            onChange={onChangeOldPassword}
            required
            autoComplete={"password"}
            inputProps={{ type: "password" }}
          />
          <InputLabel htmlFor={"new-password"}>رمز عبور جدید</InputLabel>
          <OutlinedInput
            id={"new-password"}
            value={password}
            onChange={onChangePassword}
            required
            autoComplete={"new-password"}
            inputProps={{ type: "password" }}
          />
          <InputLabel htmlFor={"new-password-confirm"}>
            تکرار رمز عبور جدید
          </InputLabel>
          <OutlinedInput
            id={"new-password-confirm"}
            value={passwordConfirm}
            onChange={onChangePasswordConfirm}
            required
            autoComplete={"new-password"}
            inputProps={{ type: "password" }}
            error={
              password.length > 0 &&
              passwordConfirm.length > 0 &&
              password !== passwordConfirm
            }
          />
        </Stack>
        <Stack direction={"row"} mx={1} mb={-1}>
          <Button
            onClick={onClose}
            variant={"contained"}
            color={"secondary"}
            sx={{ flex: 1, mr: 1 }}
          >
            {"انصراف"}
          </Button>
          <Button
            type={"submit"}
            variant={"contained"}
            sx={{ flex: 3 }}
            disabled={
              !user ||
              password.length === 0 ||
              password !== passwordConfirm ||
              isLoading
            }
          >
            {isLoading ? <CircularProgress size={24} /> : "ثبت تغییرات"}
          </Button>
        </Stack>
      </Stack>
    </BottomSheet>
  );
};
